/*
 * Project: bellcloud
 * File: apps/frontend/src/utils/monitoringGroupColors.ts
 * Author: SWAN
 * Created: 2025-01-27
 * Description: Monitoring Status Group Color Utilities
 */

import { SystemStatusGroup } from '@packages/types/device/common/system_status_group';
import type { ColorTokens } from '@/theme/tokens/colors';
import { startAlarmLoop } from '@/utils/alarmSound';
/**
 * SystemStatusGroup에 해당하는 색상 반환
 * @param group - SystemStatusGroup
 * @param colors - ColorTokens (theme에서 가져온 colors)
 * @param variant - 'default' | 'light' (기본 색상 또는 밝은 배경 색상)
 * @returns 색상 문자열
 */
function getMonitoringGroupColor(
  group: SystemStatusGroup,
  colors: ColorTokens,
  variant: 'default' | 'light' = 'default'
): string {
  if (variant === 'light') {
    switch (group) {
      case SystemStatusGroup.GroupA:
        return colors.status.stable.fill.surface;
      case SystemStatusGroup.GroupB:
        return colors.status.caution.fill.surface;
      case SystemStatusGroup.GroupC:
        return colors.status.calib.fill.surface;
      case SystemStatusGroup.GroupD:
        return colors.status.danger.fill.surface;
      default:
        return colors.surface.layout.container;
    }
  }

  switch (group) {
    case SystemStatusGroup.GroupA:
      return colors.status.stable.fill.text;
    case SystemStatusGroup.GroupB:
      return colors.status.caution.fill.text;
    case SystemStatusGroup.GroupC:
      return colors.status.calib.fill.text;
    case SystemStatusGroup.GroupD:
      return colors.status.danger.fill.text;
    default:
      return colors.text.secondary;
  }
}

/**
 * SystemStatusGroup에 해당하는 색상과 밝은 배경 색상 반환
 * @param group - SystemStatusGroup
 * @param colors - ColorTokens (theme에서 가져온 colors)
 * @returns { color: 기본 색상, lightColor: 밝은 배경 색상 }
 */
export function getMonitoringGroupColors(
  group: SystemStatusGroup,
  colors: ColorTokens
): { color: string; lightColor: string } {
  return {
    color: getMonitoringGroupColor(group, colors, 'default'),
    lightColor: getMonitoringGroupColor(group, colors, 'light'),
  };
}

/**
 * 지도 마커용 색상 반환 (진한 배경 색상)
 * @param group - SystemStatusGroup
 * @param colors - ColorTokens (theme에서 가져온 colors)
 * @returns 마커에 사용할 색상 문자열
 */
export function getMonitoringMarkerColor(group: SystemStatusGroup, colors: ColorTokens): string {
  switch (group) {
    case SystemStatusGroup.GroupA:
      return colors.status.stable.fill.surface;
    case SystemStatusGroup.GroupB:
      return colors.status.caution.fill.surface;
    case SystemStatusGroup.GroupC:
      return colors.status.calib.fill.surface;
    case SystemStatusGroup.GroupD:
      return colors.status.danger.fill.surface;
    default:
      return colors.surface.interactive.hover;
  }
}

/** FCM / 푸시 이력: 알람(붉은색) vs 경고(주황) 구분 */
type PushNotificationSeverity = 'alarm' | 'warning' | 'neutral';

function normalizePushType(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.toLowerCase().trim();
}

/**
 * data.type, notification_type, i18n_title 순으로 알람/경고 판별
 * - alarm, alarm_end → alarm (붉은 계열)
 * - warning → warning (주황 계열)
 */
export function getPushNotificationSeverity(
  data?: Record<string, unknown> | null,
  notificationType?: string | null
): PushNotificationSeverity {
  const fromData = normalizePushType(data?.type);
  const fromRow = normalizePushType(notificationType);
  const t = fromData || fromRow;

  if (t === 'alarm' || t === 'alarm_end') return 'alarm';
  if (t === 'warning') return 'warning';

  const i18nTitle = typeof data?.i18n_title === 'string' ? data.i18n_title : '';
  if (i18nTitle.includes('title_warning')) return 'warning';
  if (i18nTitle.includes('title_alarm')) return 'alarm';

  return 'neutral';
}

/** 경보음을 울릴 가스 알람 타입 (발생 시에만, 해제는 제외) */
const GAS_ALARM_TYPES = new Set(['low_alarm', 'high_alarm', 'stel_alarm', 'twa_alarm']);

/**
 * low/high/twa/stel 알람이 "발생"한 푸시인지 판별.
 * - data.type === 'alarm' (발생) 이고 data.alarm_type 이 4종 중 하나일 때만 true.
 * - data.type === 'alarm_end' (해제) 또는 fault/over-range 등은 false.
 */
export function isGasAlarmActivation(data?: Record<string, unknown> | null): boolean {
  if (!data) return false;
  if (normalizePushType(data.type) !== 'alarm') return false;
  const alarmType = normalizePushType(data.alarm_type ?? data.alarmType);
  return GAS_ALARM_TYPES.has(alarmType);
}

/**
 * 표·카드 등 밝은 배경 위 푸시 알림 강조색.
 * fill.text는 채도 높은 배경 위용(흰 글자)이라 테이블에 쓰면 배경과 구분이 안 됨 → stroke.text 사용.
 */
export function getPushNotificationAccentColor(
  colors: ColorTokens,
  severity: PushNotificationSeverity
): string {
  if (severity === 'alarm') {
    return colors.status.danger.stroke.text;
  }
  if (severity === 'warning') {
    return colors.status.caution.stroke.text;
  }
  return colors.text.primary;
}

type PushToastPosition = 'top-center';

type PushToastFn = (
  msg: string,
  opts?: {
    description?: string;
    duration?: number;
    position?: PushToastPosition;
    className?: string;
  }
) => unknown;

/**
 * 포그라운드/백그라운드 푸시 토스트 표시 (sonner 타입별 색상)
 */
export function showPushNotificationToast(
  toastFns: {
    error: PushToastFn;
    warning: PushToastFn;
    info: PushToastFn;
  },
  title: string,
  body: string | undefined,
  data: Record<string, unknown> | undefined,
  options: { duration?: number }
): unknown {
  const severity = getPushNotificationSeverity(data);

  // 토스트와 동시에 경보음 루프 시작 (집계 폴링 지연 없이 즉시 반응).
  // 이후 라이브 집계(useActiveAlarm) 상태가 루프 유지/정지를 관장한다.
  if (isGasAlarmActivation(data)) {
    startAlarmLoop();
  }

  const opts = {
    description: body,
    duration: options.duration ?? 8000,
    position: 'top-center' as const,
    className: 'push-notification-toast',
  };
  if (severity === 'alarm') {
    return toastFns.error(title, opts);
  }
  if (severity === 'warning') {
    return toastFns.warning(title, opts);
  }
  return toastFns.info(title, opts);
}
